import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories, Category } from "@/mocks/categories";
import { getArticlesByCategory, Article } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || "fuwu");
  const [displayArticles, setDisplayArticles] = useState<Article[]>([]);
  const [currentCategory, setCurrentCategory] = useState<Category | undefined>();

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setDisplayArticles(getArticlesByCategory(activeCategory));
    setCurrentCategory(categories.find((c) => c.slug === activeCategory));
  }, [activeCategory]);

  const handleCategoryChange = (categorySlug: string) => {
    setActiveCategory(categorySlug);
    window.history.replaceState(null, "", `/category/${categorySlug}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory?.name || "内容分类" },
              ]}
            />
            <div className="px-4 md:px-6 pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                {currentCategory?.name || "内容分类"}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">
                {currentCategory?.description || ""}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2 py-6 border-b border-background-200">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap cursor-pointer rounded-full transition-all duration-200 ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {cat.name}
                <span className="ml-1.5 opacity-70 text-xs">({cat.articleCount})</span>
              </button>
            ))}
          </div>

          <div className="py-10">
            {displayArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {displayArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer border border-background-200 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 91.cn`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-200 text-foreground-600 whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl" />
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${currentCategory?.name || "内容分类"} - 91.cn`,
          "description": currentCategory?.description || "",
          "url": `https://www.91.cn/category/${activeCategory}`,
          "isPartOf": {
            "@type": "WebSite",
            "name": "91.cn",
            "url": "https://www.91.cn"
          }
        })}
      </script>
    </>
  );
}